import React, { useState, useEffect, Fragment } from "react";

import { observer } from "mobx-react-lite";
import { makeStyles } from "@mui/styles";
import { useStores } from "../../../core/stores/index";
import "./UserList.css";
import { Button, MenuItem, Select, SelectChangeEvent, TextField } from "@mui/material";
import { IUser } from "common/interfaces/IUser";
import { Navigate, useNavigate } from "react-router-dom";
import { useMsal } from "@azure/msal-react";
import { DataGrid, GridColDef } from "@mui/x-data-grid";
import { useSnackbar } from "notistack";
import { Spinner } from "components/common/Spinner";

interface UserListProp {
    children: JSX.Element;
}

export const UserList = observer((props: UserListProp) => {
    const snackbar = useSnackbar();
    const { userStore } = useStores();
    const navigate = useNavigate();
    const [usersFilterSelection, setUsersFilterSelection] = React.useState("Show All");
    const [isUsersLoading, setIsUsersLoading] = useState<boolean>(true);

    const [appUsers, setUsers] = React.useState<IUser[]>(userStore.users ?? []);

    const handleUserSelectionChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
        setUsersFilterSelection(e.target.value as string);
    };

    const columns: GridColDef[] = [
        {
            field: "name",
            headerName: "Name",
            width: 300,
            renderCell: (params) => (
                <a className="hyperink" href="#" onClick={() => userClickHandler(params.row.userId)}>
                    {params.row.name}
                </a>
            ),
        },
        {
            field: "userType",
            headerName: "Role",
            width: 300,
            editable: true,
        },
        {
            field: "isEnabled",
            headerName: "Active",
            width: 300,
            editable: true,
            valueGetter: (value, row) => (row.isEnabled ? "Yes" : "No"),
        },
        {
            field: "delegationUser",
            headerName: "Delegation",
            sortable: false,
            width: 300,
            valueGetter: (value, row) => (row.delegatedUser ? `${row.delegatedUser}` : ""),
        },
    ];

    useEffect(() => {
        if (userStore.isUserOperation) {
            snackbar.enqueueSnackbar(userStore.userOperationMessage, {
                variant: userStore.messageVariant,
            });

            userStore.clearUserOperationFlags();
        }
    }, []);

    useEffect(() => {
        userStore.getAllUsers().then((response) => {
            const filteredUsers = usersFilterSelection === "Enabled Only" ? userStore.users.filter((x) => x.isEnabled == true) : userStore.users;
            setUsers(filteredUsers);
            if (appUsers.length > 0) {
                console.log(appUsers);
            }

            if (appUsers.length > 0 && isUsersLoading) {
                setIsUsersLoading(false);
            }
        });
    }, [usersFilterSelection]);

    useEffect(() => {
        userStore.getAllUsers().then((response) => {
            setUsers(userStore.users);
            if (appUsers.length > 0 && isUsersLoading) {
                setIsUsersLoading(false);
            }
        });
    }, []);

    const userClickHandler = async (userId: number | undefined) => {
        await userStore.resetSelectedUser();
        await userStore.getUserById(userId as number);
        navigate(`/addEditUser/${userId}`);
    };

    const addUserClickHandler = async () => {
        await userStore.resetSelectedUser();
        navigate("/newuser");
    };

    const searchUsers = (event: any) => {
        if (event.target.value.length >= 2) {
            const filteredUsers = userStore.users.filter((x) => x.name?.toLowerCase().includes(event.target.value.toLowerCase()));
            setUsers(filteredUsers);
        } else {
            setUsers(userStore.users);
        }
    };

    return (
        <div className="container-fluid p-1">
            <div className="m-4 card">
                <div className="card-header">
                    <div className="row">
                        <div className="col-6 align-content-center">
                            <h1>Users</h1>
                        </div>

                        <div className="col-sm-auto align-content-center">
                            <div className="p-2">
                                <button className="btn btn-primary mr-2" title="Add User" onClick={addUserClickHandler}>
                                    {"Add user"}
                                </button>
                            </div>
                        </div>
                        <div className="col-sm-auto align-content-center">
                            <select
                                value={usersFilterSelection}
                                onChange={(e) => handleUserSelectionChange(e)}
                                className="form-control w-auto mr-sm-2"
                                name="Filter"
                            >
                                <option value={"Show All"}>Show All</option>
                                <option value={"Enabled Only"}>Enabled Only</option>
                            </select>
                        </div>
                        <div className="col-sm-2 align-content-center">
                            <input type="search" onChange={searchUsers} className="form-control mr-sm-2" id="searchText" placeholder="search" />
                        </div>
                    </div>
                </div>
                <div className="row card-body w-100 h-100">
                    <div className="col table-responsive p-2">
                        {appUsers.length > 0 && (
                            <DataGrid
                                sx={{
                                    ".MuiDataGrid-columnHeaderTitle": {
                                        fontWeight: "600 !important",
                                    },
                                    ".MuiDataGrid-cell": {
                                        fontWeight: "400 !important",
                                    },
                                }}
                                getRowId={(row: IUser) => row.userId as number}
                                rows={appUsers}
                                columns={columns}
                                initialState={{
                                    pagination: {
                                        paginationModel: {
                                            pageSize: 30,
                                        },
                                    },
                                }}
                                disableRowSelectionOnClick
                            />
                        )}
                        {appUsers.length == 0 && <Spinner />}
                    </div>
                </div>
            </div>
        </div>
    );
});
